import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';

/* eslint-disable react/jsx-props-no-spreading */
import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { MyPageTopicGroupDetail, WaterfallPostTypeEnum } from '@/Interfaces/I_MyPage';

import {
  getMyPageTopicGroups,
  getMyPageTopics,
  updateMyPageTopicGroups,
} from '@/Redux/slices/MyPageSlice';

interface DraggableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const DraggableRow = ({ children, ...props }: DraggableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === 'sort') {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{ touchAction: 'none', cursor: 'move' }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const MyPageTopicGroups = () => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.myPage);
  const { topicGroups, topics } = useAppSelector((state) => state.myPage.value);
  const [form] = useForm();
  const [modalForm] = useForm();

  const [topicGroupRecords, setTopicGroupRecords] = useState<MyPageTopicGroupDetail[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const topicOptions = useMemo(
    () =>
      topics.map((topic) => ({
        label: topic,
        value: topic,
      })),
    [topics],
  );

  const resetRecords = useCallback(() => {
    setTopicGroupRecords(topicGroups.toSorted((a, b) => a.priority - b.priority));
  }, [topicGroups]);

  useEffect(() => {
    dispatch(getMyPageTopicGroups());
    dispatch(getMyPageTopics());
  }, [dispatch]);

  useEffect(() => {
    resetRecords();
  }, [resetRecords]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setTopicGroupRecords((previous) => {
        const activeIndex = previous.findIndex((i) => i.priority === active.id);
        const overIndex = previous.findIndex((i) => i.priority === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const editTopicGroup = (row: number) => {
    setEditingRow(row);
    const category = topicGroupRecords[row];
    form.setFieldsValue({
      nameZh: category.nameZh,
      nameEn: category.nameEn,
      topics: category.topics,
      disabled: category.disabled,
      type: category.type,
    });
  };

  const saveEdit = () => {
    const formValue = form.getFieldsValue(true);

    setTopicGroupRecords((prev) =>
      prev.map((topicGroup, index) => {
        if (index === editingRow) {
          return {
            ...topicGroup,
            nameZh: formValue.nameZh,
            nameEn: formValue.nameEn,
            disabled: formValue.disabled,
            topics: formValue.topics || topicGroup.topics,
            type: formValue.type,
          };
        }

        return topicGroup;
      }),
    );

    setEditingRow(null);
  };

  const deleteTopicGroup = (row: number) => {
    setTopicGroupRecords((prev) => prev.filter((_, index) => index !== row));
  };

  const addNewTopicGroup = async () => {
    try {
      const formValue = await modalForm.validateFields();

      const maxPriority = Math.max(...topicGroupRecords.map((t) => t.priority));

      setTopicGroupRecords([
        ...topicGroupRecords,
        {
          nameZh: formValue.nameZh,
          nameEn: formValue.nameEn,
          priority: maxPriority + 1,
          disabled: formValue.disabled,
          topics: formValue.topics || [],
          type: formValue.type || null,
        },
      ]);

      setModalOpen(false);
    } catch (errorInfo) {
      /* empty */
    }
  };

  const updateTopicGroups = () => {
    dispatch(updateMyPageTopicGroups(topicGroupRecords));
  };

  const columns = [
    {
      dataIndex: 'dnd',
      align: 'center' as const,
      key: 'sort',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      align: 'center' as const,
    },
    {
      title: 'NameZh',
      dataIndex: 'nameZh',
      align: 'center' as const,
      render: (nameZh: string, _r: unknown, index: number) =>
        editingRow === index ? (
          <Form.Item name="nameZh" noStyle>
            <Input defaultValue={nameZh} />
          </Form.Item>
        ) : (
          <span>{nameZh}</span>
        ),
    },
    {
      title: 'NameEn',
      dataIndex: 'nameEn',
      align: 'center' as const,
      render: (nameEn: string, _r: unknown, index: number) =>
        editingRow === index ? (
          <Form.Item name="nameEn" noStyle>
            <Input defaultValue={nameEn} />
          </Form.Item>
        ) : (
          <span>{nameEn}</span>
        ),
    },
    {
      title: 'Topics',
      dataIndex: 'topics',
      align: 'center' as const,
      render: (relatedTopics: string[], _r: unknown, index: number) =>
        index === editingRow ? (
          <Form.Item name="topics" noStyle>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Please select"
              defaultValue={relatedTopics}
              options={topicOptions}
              disabled={index !== editingRow}
            />
          </Form.Item>
        ) : (
          <span>
            {relatedTopics.map((topic) => (
              <Tag key={topic}>{topic}</Tag>
            ))}
          </span>
        ),
    },
    {
      title: 'Disabled',
      dataIndex: 'disabled',
      align: 'center' as const,
      render: (value: boolean, _r: MyPageTopicGroupDetail, index: number) =>
        index === editingRow ? (
          <Form.Item name="disabled" noStyle>
            <Switch defaultChecked={value} />
          </Form.Item>
        ) : (
          <Switch checked={value} disabled />
        ),
    },
    {
      title: 'Waterfall Post Type',
      dataIndex: 'type',
      align: 'center' as const,
      render: (type: string, _r: unknown, index: number) =>
        index === editingRow ? (
          <Form.Item name="type" noStyle>
            <Select
              style={{ width: '100%' }}
              placeholder="Please select"
              defaultValue={type}
              options={[
                { label: 'Please select', value: null },
                ...Object.entries(WaterfallPostTypeEnum).map(([key, value]) => ({
                  label: key,
                  value,
                })),
              ]}
              disabled={index !== editingRow}
            />
          </Form.Item>
        ) : (
          <span>{type}</span>
        ),
    },
    {
      title: 'Operation',
      dataIndex: 'operation',
      align: 'center' as const,
      render: (_v: unknown, _r: unknown, index: number) => (
        <div className="flex justify-center items-center gap-4">
          {editingRow === index ? (
            <>
              <Typography.Link onClick={() => saveEdit()}>Confirm</Typography.Link>
              <Popconfirm title="Sure to cancel?" onConfirm={() => setEditingRow(null)}>
                <Typography.Link>Cancel</Typography.Link>
              </Popconfirm>
            </>
          ) : (
            <Typography.Link onClick={() => editTopicGroup(index)}>Edit</Typography.Link>
          )}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'delete',
      align: 'center' as const,
      render: (_v: unknown, _r: MyPageTopicGroupDetail, index: number) => (
        <Popconfirm title="Sure to Delete?" onConfirm={() => deleteTopicGroup(index)}>
          <DeleteOutlined className="cursor-pointer" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-end gap-4">
          <Button
            onClick={() =>
              setTopicGroupRecords(
                topicGroups
                  .toSorted((a, b) => a.priority - b.priority)
                  .map((t) => ({ ...t, key: t.priority })),
              )
            }
          >
            Reset
          </Button>
          <Button onClick={() => setModalOpen(true)}>New Topic Group</Button>
          <Button type="primary" onClick={updateTopicGroups}>
            Update
          </Button>
        </div>
        <Form form={form} component={false}>
          <DndContext onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
            <SortableContext
              items={topicGroupRecords.map((t) => t.priority)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                rowKey="priority"
                components={{ body: { row: DraggableRow } }}
                dataSource={topicGroupRecords}
                columns={columns}
                loading={loading}
                pagination={false}
              />
            </SortableContext>
          </DndContext>
        </Form>
      </div>
      <Modal
        title="New MyPage Topic Group"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
        onOk={addNewTopicGroup}
      >
        <Form form={modalForm} preserve={false} layout="vertical">
          <Form.Item
            name="nameZh"
            label="Name (Chinese)"
            required
            rules={[{ required: true, message: 'Please input category name!' }]}
          >
            <Input placeholder="MyPage主題組別" />
          </Form.Item>
          <Form.Item
            name="nameEn"
            label="Name (English)"
            required
            rules={[{ required: true, message: 'Please input category name!' }]}
          >
            <Input placeholder="MyPage Topic Group" />
          </Form.Item>
          <Form.Item name="topics" label="Topics">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Please select"
              options={topicOptions}
            />
          </Form.Item>
          <Form.Item name="disabled" label="Disabeld" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="type" label="Waterfall Post Type" rules={[{ required: false }]}>
            <Select
              placeholder="Please select"
              options={[
                { label: 'Please select', value: null },
                ...Object.entries(WaterfallPostTypeEnum).map(([key, value]) => ({
                  label: key,
                  value,
                })),
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MyPageTopicGroups;
